import { useState } from "react";

import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { PageHeader, SectionCard, StatTile } from "@/features/power_projects/components/ui";

import {
  useOverheadConfig,
  useUpdateOverheadConfig,
  useUpdateProjectStatus,
  useUpdateZeShare,
  useZeShares,
} from "../hooks";
import type { EmployeeStatusRow, ZeShareRow } from "../types";
import { fmtPln, HELP, InfoDot } from "../ui";

const SHARE_SOURCE_LABEL: Record<ZeShareRow["source"], string> = {
  manual: "ręczny zapis",
  default: "domyślny 100%",
  pre_ze: "0% (przed startem ZE)",
};

function ZeShareRowEditor({ row, year, month }: { row: ZeShareRow; year: number; month: number }) {
  const update = useUpdateZeShare();
  // suwak: startuje z wartości efektywnej (default 100), zapis dopiero po „Zapisz"
  const [val, setVal] = useState<number | null>(null);
  const effective = Math.round(Number(row.share_pct));
  const current = val ?? effective;
  const dirty = val !== null && val !== effective;
  return (
    <tr className="border-b border-[var(--color-border)]">
      <td className="py-2 pr-3">{row.name}</td>
      <td className="py-2 pr-3 text-right tabular-nums font-medium w-20">{current}%</td>
      <td className={`py-2 pr-3 text-xs ${row.source === "pre_ze" ? "text-amber-600" : "text-gray-500"}`}>
        {SHARE_SOURCE_LABEL[row.source]}
      </td>
      <td className="py-2">
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={current}
            onChange={(e) => setVal(Number(e.target.value))}
            className="w-48 accent-sky-600"
          />
          <Button
            variant="secondary"
            disabled={update.isPending || !dirty}
            onClick={() => {
              update.mutate({
                designer: row.designer_id, year, month, share_pct: String(current),
              });
              setVal(null);
            }}
          >
            Zapisz
          </Button>
          {row.manual_pct != null && (
            <Button
              variant="secondary"
              disabled={update.isPending}
              onClick={() => {
                setVal(null);
                update.mutate({ designer: row.designer_id, year, month, share_pct: null });
              }}
            >
              Wyczyść (→ 100%)
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

const STATUS_SOURCE_LABEL: Record<EmployeeStatusRow["source"], string> = {
  override_this_month: "nadpisanie w tym miesiącu",
  override_carry: "nadpisanie wcześniejsze (carry-forward)",
  default: "domyślny z profilu",
};

function EmployeeStatusRowEditor({
  row, year, month,
}: { row: EmployeeStatusRow; year: number; month: number }) {
  const update = useUpdateProjectStatus();
  return (
    <tr className="border-b border-[var(--color-border)]">
      <td className="py-2 pr-3">
        {row.name}
        {row.is_ze_designer && (
          <span className="ml-1.5 text-[10px] font-mono font-bold text-sky-600">ZE</span>
        )}
      </td>
      <td className="py-2 pr-3">
        <span className={`inline-block text-[11px] font-semibold rounded px-1.5 py-0.5 ${
          row.is_non_project
            ? "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300"
            : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
        }`}>
          {row.is_non_project ? "nieprojektowy (→ narzut)" : "projektowy"}
        </span>
      </td>
      <td className="py-2 pr-3 text-xs text-gray-500">{STATUS_SOURCE_LABEL[row.source]}</td>
      <td className="py-2">
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            disabled={update.isPending}
            onClick={() =>
              update.mutate({
                employee: row.employee_id, year, month,
                is_non_project: !row.is_non_project,
              })
            }
          >
            Przełącz na {row.is_non_project ? "projektowy" : "nieprojektowy"}
          </Button>
          {row.source === "override_this_month" && (
            <Button
              variant="secondary"
              disabled={update.isPending}
              onClick={() =>
                update.mutate({
                  employee: row.employee_id, year, month, is_non_project: null,
                })
              }
            >
              Wyczyść nadpisanie
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

const MONTHS = ["", "styczeń", "luty", "marzec", "kwiecień", "maj", "czerwiec",
  "lipiec", "sierpień", "wrzesień", "październik", "listopad", "grudzień"];

export default function FinAronOverheadPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const { data, isLoading } = useOverheadConfig(year, month);
  const shares = useZeShares(year, month);
  const update = useUpdateOverheadConfig();

  const [narzut, setNarzut] = useState<string>("");
  const [window, setWindow] = useState<string>("");

  if (isLoading || !data) return <PageLoader />;

  const save = () => {
    update.mutate({
      year,
      month,
      narzut_pct: narzut.trim() === "" ? null : narzut.trim(),
      window_months: window.trim() === "" ? data.auto.window_months : Number(window),
    });
  };

  const shiftMonth = (delta: number) => {
    const idx = year * 12 + (month - 1) + delta;
    setYear(Math.floor(idx / 12));
    setMonth((idx % 12) + 1);
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <PageHeader
        title="Narzuty — konfiguracja kosztorysowa"
        subtitle="Baza = pensje koordynatorów ZE × udział ZE · narzut = pensje pozostałych + koszty stałe/zmienne nieprzypisane do przedsięwzięć"
        backTo="/fin-aron"
        backLabel="Koordynatorzy × marże"
        actions={
          <div className="flex items-center gap-2 text-sm">
            <Button variant="secondary" onClick={() => shiftMonth(-1)}>←</Button>
            <span className="font-medium min-w-36 text-center">{MONTHS[month]} {year}</span>
            <Button variant="secondary" onClick={() => shiftMonth(1)}>→</Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        <StatTile
          label={<>Narzut auto<InfoDot text={HELP.overheadBase} /></>}
          value={data.auto.auto_pct != null ? `${Number(data.auto.auto_pct).toFixed(1)}%` : "—"}
          hint={
            data.auto.future_suggested
              ? `SUGESTIA: średnia do ${data.auto.anchored_to.month}/${data.auto.anchored_to.year} · ${data.auto.designer_count} koordynatorów`
              : `okno ${data.auto.window_months} mies. · ${data.auto.designer_count} koordynatorów`
          }
        />
        <StatTile
          label="Narzut efektywny"
          value={`${Number(data.effective_narzut_pct).toFixed(1)}%`}
          tone={data.narzut_override_pct != null ? "warn" : "blue"}
          hint={data.narzut_override_pct != null ? "nadpisany ręcznie" : "z auto-propozycji"}
        />
        <StatTile
          label="Obowiązuje od"
          value={data.config_from ? `${data.config_from.month}/${data.config_from.year}` : "—"}
          hint="carry-forward: ostatni wpis ≤ miesiąc"
        />
      </div>

      <SectionCard
        title={<>Wyprowadzenie auto-propozycji (jak w kosztorysie)<InfoDot text={HELP.overheadFuture} /></>}
        className="mb-5"
        bodyClassName="overflow-x-auto"
      >
        {data.auto.future_suggested && (
          <p className="text-xs text-amber-600 mb-2">
            Miesiąc przyszły — propozycja to sugestia ze średniej ostatnich {data.auto.window_months} mies.
            (okno kończy się na {data.auto.anchored_to.month}/{data.auto.anchored_to.year}).
          </p>
        )}
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-[var(--color-border)]">
              <th className="py-2 pr-3">Miesiąc</th>
              <th className="py-2 pr-3 text-right">Baza ZE<InfoDot text={HELP.zeShare} /></th>
              <th className="py-2 pr-3 text-right">Pensje projektowe ogółem</th>
              <th className="py-2 pr-3 text-right">Udział ZE<InfoDot text={HELP.overheadBase} /></th>
              <th className="py-2 pr-3 text-right">Pensje nie-projektowych</th>
              <th className="py-2 pr-3 text-right">Koszty nieprzypisane</th>
              <th className="py-2 pr-3 text-right">Pula wspólna</th>
              <th className="py-2 pr-3 text-right">Pula ZE (proporcja)</th>
              <th className="py-2 text-right">Narzut %</th>
            </tr>
          </thead>
          <tbody>
            {data.auto.months.map((m) => (
              <tr key={`${m.year}-${m.month}`} className="border-b border-[var(--color-border)]">
                <td className="py-2 pr-3">{m.month}/{m.year}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{fmtPln(m.designer_salaries)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{fmtPln(m.project_salaries_total)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {m.ze_share_of_project_pct != null ? `${Number(m.ze_share_of_project_pct).toFixed(1)}%` : "—"}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">{fmtPln(m.non_project_salaries)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {fmtPln(String(Number(m.fixed_recurring) + Number(m.fixed_onetime)))}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">{fmtPln(m.common_pool)}</td>
                <td className="py-2 pr-3 text-right tabular-nums font-medium">{fmtPln(m.pool)}</td>
                <td className="py-2 text-right tabular-nums font-medium">
                  {m.pct != null ? `${Number(m.pct).toFixed(1)}%` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-gray-400 mt-2">{data.auto.note}</p>
      </SectionCard>

      <SectionCard
        title={<>Udział ZE koordynatorów — {MONTHS[month]} {year}<InfoDot text={HELP.zeShare} /></>}
        className="mb-5"
        bodyClassName="overflow-x-auto"
      >
        {shares.isLoading || !shares.data ? (
          <PageLoader />
        ) : shares.data.designers.length === 0 ? (
          <p className="text-sm text-gray-400">Brak koordynatorów ZE.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-[var(--color-border)]">
                <th className="py-2 pr-3">Koordynator</th>
                <th className="py-2 pr-3 text-right">Udział ZE</th>
                <th className="py-2 pr-3">Źródło</th>
                <th className="py-2">Suwak (default 100% — ile etatu na ZE vs inne projekty)</th>
              </tr>
            </thead>
            <tbody>
              {shares.data.designers.map((row) => (
                <ZeShareRowEditor key={row.designer_id} row={row} year={year} month={month} />
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      <SectionCard
        title={<>Status projektowy / nieprojektowy — {MONTHS[month]} {year}<InfoDot text={HELP.projectStatus} /></>}
        className="mb-5"
        bodyClassName="overflow-x-auto"
      >
        {shares.isLoading || !shares.data ? (
          <PageLoader />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-[var(--color-border)]">
                <th className="py-2 pr-3">Pracownik</th>
                <th className="py-2 pr-3">Status (efektywny)</th>
                <th className="py-2 pr-3">Źródło</th>
                <th className="py-2">Edycja (obowiązuje od tego miesiąca — carry-forward)</th>
              </tr>
            </thead>
            <tbody>
              {shares.data.employees.map((row) => (
                <EmployeeStatusRowEditor key={row.employee_id} row={row} year={year} month={month} />
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      <SectionCard title={`Ręczne nadpisanie od ${MONTHS[month]} ${year}`}>
        <div className="flex flex-wrap items-end gap-4">
          <label className="text-sm">
            <span className="block text-gray-500 mb-1">Narzut % (puste = auto)</span>
            <input
              value={narzut}
              onChange={(e) => setNarzut(e.target.value)}
              placeholder={data.narzut_override_pct ?? "auto"}
              className="border border-[var(--color-border)] rounded px-2 py-1.5 w-36 bg-transparent"
            />
          </label>
          <label className="text-sm">
            <span className="block text-gray-500 mb-1">Okno auto (mies.)</span>
            <input
              value={window}
              onChange={(e) => setWindow(e.target.value)}
              placeholder={String(data.auto.window_months)}
              className="border border-[var(--color-border)] rounded px-2 py-1.5 w-28 bg-transparent"
            />
          </label>
          <Button onClick={save} disabled={update.isPending}>
            {update.isPending ? "Zapisywanie…" : "Zapisz"}
          </Button>
        </div>
        {update.isError && (
          <p className="text-sm text-red-600 mt-2">Nie udało się zapisać — sprawdź wartości (0–500%).</p>
        )}
      </SectionCard>
    </div>
  );
}
